import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pipelineApi } from '../services/pipelineApi';
import { dealKeys } from './useDeals';
import type { CustomerSegment, PipelineCreate, PipelineUpdate, StageCreate, StageUpdate } from '../types';

export const pipelineKeys = {
  all: ['pipelines'] as const,
  list: () => [...pipelineKeys.all, 'list'] as const,
  detail: (id: string) => [...pipelineKeys.all, 'detail', id] as const,
  summary: (id: string) => [...pipelineKeys.all, 'summary', id] as const,
  defaults: (segment?: CustomerSegment, language?: string) =>
    [...pipelineKeys.all, 'defaults', segment ?? 'tenant', language ?? 'en'] as const,
};

export function usePipelines(includeCriteria = false) {
  return useQuery({
    queryKey: [...pipelineKeys.list(), { includeCriteria }] as const,
    queryFn: () => pipelineApi.list(includeCriteria),
  });
}

export function usePipeline(id: string) {
  return useQuery({
    queryKey: pipelineKeys.detail(id),
    queryFn: () => pipelineApi.get(id),
    enabled: !!id,
  });
}

export function usePipelineSummary(id: string) {
  return useQuery({
    queryKey: pipelineKeys.summary(id),
    queryFn: () => pipelineApi.getSummary(id),
    enabled: !!id,
  });
}

/**
 * Default pipeline preset (name, description, stages) for a segment.
 *
 * Omit `segment` to get the tenant's customer-type-derived default (its
 * `customer_segment` field tells the create dialogs which segment to pre-select).
 * Pass an explicit segment to seed that segment's stages regardless of the
 * tenant default.
 *
 * The preset's name/description/stage names come back translated into the
 * current UI language, so a pipeline created from these defaults reads in the
 * user's language rather than English.
 */
export function usePipelineDefaults(segment?: CustomerSegment) {
  const { i18n } = useTranslation();
  const language = i18n.language;
  return useQuery({
    queryKey: pipelineKeys.defaults(segment, language),
    queryFn: () => pipelineApi.getDefaults(segment, language),
    staleTime: 5 * 60 * 1000, // 5 minutes - defaults rarely change
  });
}

export function useCreatePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PipelineCreate) => pipelineApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
    },
  });
}

export function useUpdatePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PipelineUpdate }) =>
      pipelineApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
    },
  });
}

export function useDeletePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, moveDealsTo }: { id: string; moveDealsTo?: string }) =>
      pipelineApi.delete(id, moveDealsTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
      // A move re-homes deals onto ANOTHER pipeline, so every deal list, board
      // and summary is stale — not just the pipeline list.
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
    },
  });
}

export function useCreateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, data }: { pipelineId: string; data: StageCreate }) =>
      pipelineApi.createStage(pipelineId, data),
    onSuccess: (_, { pipelineId }) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(pipelineId) });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
    },
  });
}

export function useUpdateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId: _pipelineId, stageId, data }: { pipelineId: string; stageId: string; data: StageUpdate }) =>
      pipelineApi.updateStage(stageId, data),
    onSuccess: (_, { pipelineId }) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(pipelineId) });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
    },
  });
}

export function useDeleteStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageId }: { pipelineId: string; stageId: string }) =>
      pipelineApi.deleteStage(pipelineId, stageId),
    onSuccess: (_, { pipelineId }) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(pipelineId) });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
    },
  });
}

export function useReorderStages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageIds }: { pipelineId: string; stageIds: string[] }) =>
      pipelineApi.reorderStages(pipelineId, stageIds),
    onSuccess: (_, { pipelineId }) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(pipelineId) });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
    },
  });
}
